import * as React from 'react';
import { useTranslation } from 'next-i18next';
import tw, { css, styled } from 'twin.macro';
import { keyframes } from 'styled-components';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { animated } from '@react-spring/web';

import { GoogleAutoCompleteInput, SkeletonTextLoading, Title } from '@restworld/ui-ds';
import { isOrgOnboardingModeAtom } from '../../organization-onboarding/use-organization-onboarding';
import { activeRestaurantOnboardingStateAtom, isManualEntryAtom } from '.';
import { ManualEntryPrompt } from '../../shared/manual-venue-entry';

const defaultInputProps = {
  type: 'text',
  id: '',
};

const Wrapper = styled.div`
  ${tw`flex flex-col w-full h-full gap-6`};
`;

const inputAppear = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const inputPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(139, 92, 246, 0), 0 0 6px 0 rgba(139, 92, 246, 0.14);
  }
  50% {
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.14), 0 0 12px 1px rgba(139, 92, 246, 0.26);
  }
`;

const InputPulseWrapper = styled.div<{ $isPulsing: boolean }>`
  ${tw`w-full`}
  animation: ${inputAppear} 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;

  ${({ $isPulsing }) =>
    $isPulsing &&
    css`
      & input {
        animation: ${inputPulse} 2s ease-in-out 0.6s infinite;
      }
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    & input {
      animation: none;
      box-shadow: 0 0 6px 0 rgba(139, 92, 246, 0.16);
    }
  }
`;

export const googleAddresInputValueAtom = atom('');
export const activeAddressIdAtom = atom('');
export const activeRestaurantAtom = atom('');
// Transient flag: when true, the Google address input mounts with autoFocus.
// Set by the intro CTA handler so the search input is focused immediately on
// transition from the intro screen (mobile keyboard opens).
export const googleAddressInputAutoFocusAtom = atom(false);

interface IGoogleAddressInputResultProps {
  /**
   * Replaces the default OnboardingTitle when the input is empty.
   * Pass `null` to render no title at all. Used by the getting-started intro
   * A/B experiment to swap the standard title for the variant-A pitch.
   */
  titleOverride?: React.ReactNode;
  /**
   * Optional content rendered immediately above the input with a tight 8 px
   * gap (separate from the wrapper's 24 px gap that applies to titleOverride).
   * Used by the getting-started intro A/B experiment to place the input label
   * right above the search box.
   */
  aboveInputSlot?: React.ReactNode;
  /**
   * Optional content rendered below the input when it's empty (same gating as
   * OnboardingTitle). Used by the getting-started intro A/B experiment to
   * render the "How it works" section + step list under the search.
   */
  footerSlot?: React.ReactNode;
}

const GoogleAddressInputResult: React.FunctionComponent<IGoogleAddressInputResultProps> = ({
  titleOverride,
  aboveInputSlot,
  footerSlot,
}) => {
  const [activeItem, setActiveItem] = useAtom(activeAddressIdAtom);
  const [inputValue, setInputValue] = useAtom(googleAddresInputValueAtom);
  const setActiveRestaurantName = useSetAtom(activeRestaurantAtom);
  const setShouldAutoFocus = useSetAtom(googleAddressInputAutoFocusAtom);

  const inputRef = React.useRef<HTMLDivElement | null>(null);
  const isInputFilled = inputValue.length > 0;

  // Always focus the search input on mount so the user can start typing
  // immediately. Reset the transient atom flag after consuming it.
  React.useEffect(() => {
    setShouldAutoFocus(false);
    const inputEl = inputRef.current?.querySelector<HTMLInputElement>('input');
    inputEl?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToInput = React.useCallback((element: HTMLDivElement) => {
    const rect = element.getBoundingClientRect();
    const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

    if (!isVisible) {
      setTimeout(() => {
        if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          window.scrollTo({ top: rect.top + window.scrollY - 100, behavior: 'smooth' });
        } else {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 750);
    }
  }, []);

  React.useEffect(() => {
    if (isInputFilled && inputRef.current) scrollToInput(inputRef.current);
  }, [isInputFilled, scrollToInput]);

  const handleInputChange = React.useCallback(
    (value: string) => {
      setInputValue(value);
      if (activeItem) setActiveItem(null);
    },
    [activeItem, setActiveItem, setInputValue]
  );

  const handleDropdownItemClick = React.useCallback(
    (selectedItem: string, restaurantName?: string) => {
      setActiveItem(selectedItem);
      setActiveRestaurantName(restaurantName);
    },
    [setActiveItem, setActiveRestaurantName]
  );

  const inputProps = {
    ...defaultInputProps,
    icon: MagnifyingGlassIcon,
    bordercolor: 'primary' as const,
    hasLeftPadding: true,
    autoFocus: false,
    wrapperStyle: tw`w-full`,
    onChange: handleInputChange,
    value: inputValue,
    isFullwidth: true,
  };

  const dropdownItemProps = {
    onClick: handleDropdownItemClick,
    activeItem,
    itemRenderVariant: 'without_break_line' as const,
    loadingElement: (
      <li tw="flex flex-col gap-4">
        <SkeletonTextLoading className="!w-full !h-[2.5rem]" width="rect-box" />
        <SkeletonTextLoading className="!w-full !h-[2.5rem]" width="rect-box" />
        <SkeletonTextLoading className="!w-full !h-[2.5rem]" width="rect-box" />
      </li>
    ),
  };

  return (
    <animated.div
      style={{
        transform: !isInputFilled ? 'translateY(4%)' : 'translateY(0%)',
        transition: 'transform 0.3s ease-in-out',
      }}
      tw="w-full"
    >
      <Wrapper>
        {!isInputFilled && (titleOverride !== undefined ? titleOverride : <OnboardingTitle />)}
        <div tw="flex flex-col gap-2 w-full">
          {!isInputFilled && aboveInputSlot}
          <InputPulseWrapper ref={inputRef} $isPulsing={!isInputFilled}>
            <GoogleAutoCompleteInput
              placesFilterTypes={['establishment']}
              formId="search-address-input"
              key="search-address-input"
              placeholder="es. Pizzeria Mozzarella, Torino"
              inputProps={inputProps}
              dropdownItemProps={dropdownItemProps}
              mainWrapperStyle={tw`w-full flex flex-col gap-8`}
              dropdownMainWrapperStyle={tw`!relative border-none shadow-none lg:(max-w-full mt-0)`}
              dropdownSubWrapperStyle={tw`max-h-full overflow-x-hidden px-0 py-0`}
              withFloatingUI={false}
              placesLocationBias={{
                south: 35.49,
                west: 6.63,
                north: 47.09,
                east: 18.52,
              }}
            />
          </InputPulseWrapper>
        </div>
        {!isInputFilled && footerSlot}
        {isInputFilled && <ManualEntryLink />}
      </Wrapper>
    </animated.div>
  );
};

const OnboardingTitle = () => {
  const { t } = useTranslation('restaurant-onboarding');
  const isOrgOnboarding = useAtomValue(isOrgOnboardingModeAtom);

  return (
    <div tw="self-start flex flex-col justify-start gap-1">
      <Title fontSize="extraLarge" fontWeight="semibold" textColor="neutral-700" content={t('let_s_get_started')} />
      <Title
        fontSize="extraLarge"
        fontWeight="semibold"
        textColor="neutral-700"
        content={t(isOrgOnboarding ? 'which_venue_are_you_looking_to_hire_staff_for' : 'search_your_restaurant_name')}
      />
      <p tw="text-base font-normal text-neutral-500 m-0 mt-2">{t('let_s_get_started_subtitle')}</p>
    </div>
  );
};

const ManualEntryLink = () => {
  const setActiveUI = useSetAtom(activeRestaurantOnboardingStateAtom);
  const setIsManualEntry = useSetAtom(isManualEntryAtom);

  const handleClick = React.useCallback(() => {
    setIsManualEntry(true);
    setActiveUI('restaurant_onboarding_form');
  }, [setActiveUI, setIsManualEntry]);

  return <ManualEntryPrompt visible onClick={handleClick} />;
};

export default GoogleAddressInputResult;
